import React from 'react';
import MDEditor, { commands } from '@uiw/react-md-editor';
import rehypeSanitize from "rehype-sanitize";

interface MarkdownFieldProps {
  value: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  minHeight?: number;
  maxLength?: number;
  label?: string;
}

export const MarkdownField: React.FC<MarkdownFieldProps> = ({ 
  value, 
  onChange, 
  placeholder, 
  minHeight = 200, 
  label 
}) => {
  const newLineCommand = {
    name: 'newLine',
    keyCommand: 'newLine',
    buttonProps: { 'aria-label': 'Insert New Line', title: 'Insert Line Break' },
    icon: (
      <svg fill="currentColor" viewBox="0 0 24 24" width="16" height="16">
        <path d="M4.64119 12.5L7.51402 15.2038C7.81565 15.4877 7.83004 15.9624 7.54615 16.264C7.26226 16.5657 6.78761 16.58 6.48598 16.2961L2.23598 12.2961C2.08539 12.1544 2 11.9568 2 11.75C2 11.5432 2.08539 11.3456 2.23598 11.2038L6.48598 7.20385C6.78761 6.91996 7.26226 6.93435 7.54615 7.23598C7.83004 7.53761 7.81565 8.01226 7.51402 8.29615L4.64118 11L14.75 11C15.7165 11 16.5 10.2165 16.5 9.25V4.75C16.5 4.33579 16.8358 4 17.25 4C17.6642 4 18 4.33579 18 4.75V9.25C18 11.0449 16.5449 12.5 14.75 12.5L4.64119 12.5Z" fill="#212121"/>
      </svg>
    ),
    execute: (state:any, api:any) => {
      const { selectedText, selection } = state;
      const modifyText = selectedText ? selectedText + '  \n' : '  \n';
      api.replaceSelection(modifyText);
    },
  };
  return (
    <div className="markdown-editor-wrapper" data-color-mode="light">
      {label && (
        <div className="headingTxt mb-2">{label}</div>
      )}
      <MDEditor
        value={value}
        onChange={onChange}
        preview="edit"
        height={minHeight}
        previewOptions={{
          rehypePlugins: [[rehypeSanitize]]
        }}
        textareaProps={{
          placeholder,
          onKeyDown: (e) => {
            const textarea = e.currentTarget;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;

            if (e.key === 'Enter' && !e.shiftKey) {
              // Override default Enter → soft line break
              e.preventDefault();
              const newValue =
                value.slice(0, start) + '  \n' + value.slice(end);
              onChange(newValue);

              requestAnimationFrame(() => {
                textarea.selectionStart = textarea.selectionEnd = start + 3;
              });
            } else if (e.key === 'Enter' && e.shiftKey) {
              // Shift+Enter → normal paragraph
              e.preventDefault();
              const newValue =
                value.slice(0, start) + '\n\n' + value.slice(end);
              onChange(newValue);

              requestAnimationFrame(() => {
                textarea.selectionStart = textarea.selectionEnd = start + 2;
              });
            }
          },
        }}
        commands={[
          ...commands.getCommands(),   // grab defaults
          newLineCommand             // add your custom one at the end
        ]}
      />
    </div>
  );
};

export const MarkdownDisplay: React.FC<{
  content: string;
  className?: string;
}> = ({ content, className = '' }) => {
  return (
    <div  className={`markdown-display ${className}`}>
      <MDEditor.Markdown 
        source={content}
        style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 'inherit', color: 'inherit' }}
        rehypePlugins={[[rehypeSanitize]]}
      />
    </div>
  );
};

export default { MarkdownField, MarkdownDisplay };